import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@repo/api-client";

interface Attendee {
  id: string;
  name: string;
  email: string;
  ticketType: string;
  checkedIn: boolean;
  checkinTime?: string;
}

// Mock attendee data — replace with API in production
const MOCK_ATTENDEES: Attendee[] = [
  { id: "TKT-001", name: "Arjun Sharma", email: "arjun@example.com", ticketType: "Sena Circle", checkedIn: false },
  { id: "TKT-002", name: "Priya Patel", email: "priya@example.com", ticketType: "Devotee", checkedIn: true, checkinTime: "09:14 AM" },
  { id: "TKT-003", name: "David Morrow", email: "david@example.com", ticketType: "PWYC", checkedIn: false },
  { id: "TKT-004", name: "Ananya Singh", email: "ananya@example.com", ticketType: "Enlightenment Pass", checkedIn: true, checkinTime: "09:22 AM" },
  { id: "TKT-005", name: "Tom Hawthorn", email: "tom@example.com", ticketType: "Devotee", checkedIn: false },
  { id: "TKT-006", name: "Meera Krishnan", email: "meera@example.com", ticketType: "Sena Circle", checkedIn: true, checkinTime: "09:05 AM" },
  { id: "TKT-007", name: "Carlos Ruiz", email: "carlos@example.com", ticketType: "PWYC", checkedIn: false },
  { id: "TKT-008", name: "Linda Webb", email: "linda@example.com", ticketType: "Devotee", checkedIn: false },
];

export function CheckInPage() {
  const [attendees, setAttendees] = useState<Attendee[]>(MOCK_ATTENDEES);
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"search" | "walkin" | "stats">("search");
  const [lastCheckedIn, setLastCheckedIn] = useState<Attendee | null>(null);

  // Active event — in production you'd pick from a list; default to astral retreat
  const EVENT_ID = "evt-001";

  // Walk-in form
  const [walkinName, setWalkinName] = useState("");
  const [walkinEmail, setWalkinEmail] = useState("");
  const [walkinTicket, setWalkinTicket] = useState("PWYC");
  const [walkinSuccess, setWalkinSuccess] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load from API on mount, fall back to mock data
  useEffect(() => {
    api.listCheckIns(EVENT_ID).then((res) => {
      if (res.attendees.length > 0) {
        setAttendees(
          res.attendees.map((a) => ({
            id: String(a.registration_id ?? a.attendee_name),
            name: a.attendee_name,
            email: "",
            ticketType: a.ticket_tier,
            checkedIn: a.checked_in,
            checkinTime: a.checked_in_at
              ? new Date(a.checked_in_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
              : undefined,
          }))
        );
      }
    }).catch(() => {/* use mock fallback */});
  }, []);

  const filtered = attendees.filter((a) => {
    const q = query.toLowerCase();
    return (
      a.name.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q) ||
      a.id.toLowerCase().includes(q)
    );
  });

  const checkedInCount = attendees.filter((a) => a.checkedIn).length;
  const pendingCount = attendees.filter((a) => !a.checkedIn).length;

  function handleCheckIn(id: string) {
    const now = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    setAttendees((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, checkedIn: true, checkinTime: now } : a
      )
    );
    const found = attendees.find((a) => a.id === id);
    if (found) setLastCheckedIn({ ...found, checkedIn: true, checkinTime: now });
    setQuery("");
    // Persist to API (fire-and-forget; UI already updated optimistically)
    api.performCheckIn(EVENT_ID, id).catch(() => {});
    setTimeout(() => setLastCheckedIn(null), 4000);
  }

  function handleWalkIn(e: React.FormEvent) {
    e.preventDefault();
    const newId = `WLK-${String(attendees.length + 1).padStart(3, "0")}`;
    const now = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    const newAttendee: Attendee = {
      id: newId,
      name: walkinName.trim(),
      email: walkinEmail.trim(),
      ticketType: walkinTicket,
      checkedIn: true,
      checkinTime: now,
    };
    setAttendees((prev) => [newAttendee, ...prev]);
    setLastCheckedIn(newAttendee);
    // Persist to API
    api.addWalkIn(EVENT_ID, { name: walkinName.trim(), email: walkinEmail.trim(), ticket_tier: walkinTicket }).catch(() => {});
    setWalkinName("");
    setWalkinEmail("");
    setWalkinTicket("PWYC");
    setWalkinSuccess(true);
    setTimeout(() => { setWalkinSuccess(false); setLastCheckedIn(null); }, 4000);
  }

  return (
    <div className="checkin-page">
      <div className="container">
        {/* Header */}
        <div className="checkin-header">
          <div>
            <span className="eyebrow">AekamPass · Check-In</span>
            <h1 className="display-3">Astral Healing Retreat</h1>
            <p className="text-muted" style={{ fontSize: "0.95rem" }}>
              May 29–31, 2026 · The Grand Oaks Resort, Weirsdale FL
            </p>
          </div>
          <Link to="/ops" className="btn btn--outline btn--sm">← Back to Ops</Link>
        </div>

        {/* Success banner */}
        {lastCheckedIn && (
          <div className="checkin-success-banner">
            <span className="checkin-success-banner__icon">✓</span>
            <div>
              <strong>{lastCheckedIn.name}</strong> has been checked in successfully!
              <span className="checkin-success-banner__ticket"> {lastCheckedIn.ticketType}</span>
            </div>
          </div>
        )}

        {/* Stats strip */}
        <div className="checkin-stats-strip">
          <div className="checkin-stat">
            <span className="checkin-stat__value">{attendees.length}</span>
            <span className="checkin-stat__label">Total Registered</span>
          </div>
          <div className="checkin-stat checkin-stat--in">
            <span className="checkin-stat__value">{checkedInCount}</span>
            <span className="checkin-stat__label">Checked In</span>
          </div>
          <div className="checkin-stat checkin-stat--pending">
            <span className="checkin-stat__value">{pendingCount}</span>
            <span className="checkin-stat__label">Pending</span>
          </div>
          <div className="checkin-stat">
            <span className="checkin-stat__value">
              {attendees.length ? Math.round((checkedInCount / attendees.length) * 100) : 0}%
            </span>
            <span className="checkin-stat__label">Attendance Rate</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="checkin-tabs">
          {(["search", "walkin", "stats"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={`checkin-tab${activeTab === tab ? " active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "search" ? "🔍 Search" : tab === "walkin" ? "➕ Walk-In" : "📊 Stats"}
            </button>
          ))}
        </div>

        {/* Search tab */}
        {activeTab === "search" && (
          <div className="checkin-search-panel">
            <div className="checkin-search-bar">
              <input
                ref={searchInputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email, or ticket ID…"
                className="checkin-search-input"
                autoFocus
              />
            </div>

            <div className="checkin-list">
              {filtered.length === 0 && (
                <div className="checkin-empty">No attendees match "{query}"</div>
              )}
              {filtered.map((a) => (
                <div key={a.id} className={`checkin-row${a.checkedIn ? " checkin-row--done" : ""}`}>
                  <div className="checkin-row__status">
                    {a.checkedIn ? (
                      <span className="badge badge--success">✓ In</span>
                    ) : (
                      <span className="badge badge--pending">Pending</span>
                    )}
                  </div>
                  <div className="checkin-row__info">
                    <div className="checkin-row__name">{a.name}</div>
                    <div className="checkin-row__meta">
                      {a.email} &nbsp;·&nbsp; <span className="checkin-row__ticket">{a.ticketType}</span>
                      &nbsp;·&nbsp; <span className="checkin-row__id">{a.id}</span>
                    </div>
                    {a.checkinTime && (
                      <div className="checkin-row__time">Checked in at {a.checkinTime}</div>
                    )}
                  </div>
                  <div className="checkin-row__action">
                    {!a.checkedIn && (
                      <button
                        type="button"
                        className="btn btn--brand btn--sm"
                        onClick={() => handleCheckIn(a.id)}
                      >
                        Check In
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Walk-in tab */}
        {activeTab === "walkin" && (
          <div className="checkin-walkin-panel">
            <h2 className="section-title" style={{ marginBottom: "1.5rem" }}>Register Walk-In Attendee</h2>
            {walkinSuccess && (
              <div className="checkin-success-banner" style={{ marginBottom: "1.5rem" }}>
                <span className="checkin-success-banner__icon">✓</span>
                <span>Walk-in registered and checked in!</span>
              </div>
            )}
            <form className="walkin-form" onSubmit={handleWalkIn}>
              <div className="form-group">
                <label className="form-label" htmlFor="walkin-name">Full Name *</label>
                <input
                  id="walkin-name"
                  type="text"
                  className="form-input"
                  value={walkinName}
                  onChange={(e) => setWalkinName(e.target.value)}
                  placeholder="Attendee full name"
                  required
                  autoComplete="name"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="walkin-email">Email Address *</label>
                <input
                  id="walkin-email"
                  type="email"
                  className="form-input"
                  value={walkinEmail}
                  onChange={(e) => setWalkinEmail(e.target.value)}
                  placeholder="attendee@example.com"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="walkin-ticket">Ticket Type</label>
                <select
                  id="walkin-ticket"
                  className="form-input"
                  value={walkinTicket}
                  onChange={(e) => setWalkinTicket(e.target.value)}
                >
                  <option value="PWYC">Pay What You Can (PWYC)</option>
                  <option value="Devotee">Devotee — $108</option>
                  <option value="Sena Circle">Sena Circle — $222</option>
                  <option value="Enlightenment Pass">Enlightenment Pass — $378</option>
                </select>
              </div>
              <button type="submit" className="btn btn--brand" style={{ marginTop: "0.5rem" }}>
                Register &amp; Check In
              </button>
            </form>
          </div>
        )}

        {/* Stats tab */}
        {activeTab === "stats" && (
          <div className="checkin-stats-panel">
            <h2 className="section-title" style={{ marginBottom: "1.5rem" }}>Attendance Breakdown</h2>
            <div className="checkin-breakdown-grid">
              {["PWYC", "Devotee", "Sena Circle", "Enlightenment Pass"].map((tier) => {
                const total = attendees.filter((a) => a.ticketType === tier).length;
                const inCount = attendees.filter((a) => a.ticketType === tier && a.checkedIn).length;
                return (
                  <div key={tier} className="checkin-breakdown-card">
                    <div className="checkin-breakdown-card__tier">{tier}</div>
                    <div className="checkin-breakdown-card__nums">
                      <span className="checkin-breakdown-card__in">{inCount}</span>
                      <span className="checkin-breakdown-card__sep">/</span>
                      <span className="checkin-breakdown-card__total">{total}</span>
                    </div>
                    <div className="checkin-progress-bar">
                      <div
                        className="checkin-progress-fill"
                        style={{ width: total ? `${(inCount / total) * 100}%` : "0%" }}
                      />
                    </div>
                    <div className="checkin-breakdown-card__pct">
                      {total ? Math.round((inCount / total) * 100) : 0}% checked in
                    </div>
                  </div>
                );
              })}
            </div>

            <h3 style={{ marginTop: "2rem", marginBottom: "1rem", fontSize: "1rem", fontWeight: 600 }}>
              Recent Check-Ins
            </h3>
            <div className="checkin-recent-list">
              {attendees
                .filter((a) => a.checkedIn && a.checkinTime)
                .slice(0, 5)
                .map((a) => (
                  <div key={a.id} className="checkin-recent-row">
                    <span className="badge badge--success">✓</span>
                    <span className="checkin-recent-name">{a.name}</span>
                    <span className="checkin-recent-ticket">{a.ticketType}</span>
                    <span className="checkin-recent-time">{a.checkinTime}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
